import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { platformKey } from "@/constants/enums";
import { fetchOffers, fetchRedemptionCounts } from "@/lib/api";
import {
  collectRedemptionReferenceIds,
  convertOffersToWorkableOffers,
  normalizeRedemptionCounts,
} from "@/lib/offers";
import type { CustomerRedemptionCounts, Offer } from "@/types/wallet";
import type { RootState } from "..";

/**
 * `state.offers` — rebuilt from the original's `offers-slice` (module 55458).
 *
 * The previous shape here (`{ available, claimed, appliedCode, error }`) was
 * invented: the original has ONE list plus the per-customer redemption counts,
 * and it has no notion of a "claimed offer" list at all — a claim lands in
 * `cart.data.redeemed_offers`, keyed by offer id, and the offer stays in the
 * same list with different stats.
 *
 * `socketFetchedOffers` is the original's only writer; ours is the REST load,
 * because the storefront has no socket. The platform filter and the `sort_id`
 * ordering live in `selectWorkableOffers`, exactly as the original's `pS`
 * selector does, so the stored list stays the raw catalogue.
 */
interface OffersState {
  data: Offer[];
  loading: boolean;
  customerOfferRedemptionCounts: CustomerRedemptionCounts;
  redemptionCountsFetched: boolean;
}

const initialState: OffersState = {
  data: [],
  loading: false,
  customerOfferRedemptionCounts: null,
  redemptionCountsFetched: false,
};

export const loadOffers = createAsyncThunk("offers/load", async (tenantId: string) =>
  fetchOffers(tenantId),
);

/**
 * `fetchOfferRedemptionCounts` — the original's one HTTP call for the wallet.
 *
 * It short-circuits on exactly the original's conditions: no brand, no tenant,
 * signed out, or an empty catalogue. `null` means "not applicable", which the
 * reducer stores as-is; the card then shows no consumed badge rather than
 * asserting zero uses.
 */
export const fetchOfferRedemptionCounts = createAsyncThunk(
  "offers/fetchOfferRedemptionCounts",
  async (_: void, { getState }) => {
    const state = getState() as RootState;
    const brandId = state.settings.data?.brand_id;
    const tenantId = state.settings.data?._id;
    const offers = state.offers.data;
    const phone = state.auth.customer?.phone_number;
    if (!brandId || !tenantId || !state.auth.customer || offers.length === 0) {
      return null;
    }
    const ids = collectRedemptionReferenceIds(offers);
    const payload = await fetchRedemptionCounts(brandId, {
      ...ids,
      phone_number: phone ?? "",
    });
    return normalizeRedemptionCounts(payload);
  },
);

const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    /** The original's `socketFetchedOffers`, fed from REST here. */
    setOffers(state, action: PayloadAction<Offer[] | null>) {
      state.data = action.payload ?? [];
    },
    resetOffers: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadOffers.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadOffers.fulfilled, (state, action) => {
        state.data = action.payload ?? [];
        state.loading = false;
      })
      .addCase(loadOffers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchOfferRedemptionCounts.fulfilled, (state, action) => {
        state.customerOfferRedemptionCounts = action.payload;
        state.redemptionCountsFetched = true;
      })
      .addCase(fetchOfferRedemptionCounts.rejected, (state) => {
        state.customerOfferRedemptionCounts = null;
        state.redemptionCountsFetched = true;
      });
  },
});

export const { setOffers, resetOffers } = offersSlice.actions;

const selectOffersData = (state: RootState) => state.offers.data;

/**
 * The original reads `cart.data.platform` (`WEB_DELIVERY`); this tree keeps the
 * order type alone and derives the platform key, which is the same value.
 */
const selectCartPlatform = (state: RootState) =>
  platformKey(state.cart.orderType);

/**
 * `pS` — the offers this cart's platform can actually see, `sort_id` ordered.
 * `null` platform yields `[]`, as in the original.
 */
export const selectWorkableOffers = createSelector(
  [selectOffersData, selectCartPlatform],
  (offers, platform) => convertOffersToWorkableOffers(offers, platform),
);

export default offersSlice.reducer;
